import { useEffect, useRef, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';

import { useTheme } from '../../context/ThemeContext';
import { useSocketIO } from '../../context/socket';
import { Ads } from './Ads';
import { CardYouTube } from './CardYouTube';
import { CardsSale } from './CardsSale';
import { extractVideoCodeYouTube } from './functions';
import type { ProductProps } from './types';

const cards = Object.keys(CardsSale) as (keyof typeof CardsSale)[];

type CurrentCardProps = (
	| ({ model: (typeof cards)[number] } & { product: ProductProps })
	| { model: 'ads'; content: any }
	| {
			model: 'youtube';
			videoId: string;
			loop?: boolean;
			onEnd?: () => void;
			id: number;
	  }
	| { model: 'intro' | 'introBusiness' }
) & { next?: () => void };

interface Props {
	hideVideos?: boolean;
}

export function ProductRotator({ hideVideos = false }: Props) {
	const { socket, statusSocket } = useSocketIO();
	const { setSettings } = useTheme();

	const [products, setProducts] = useState<ProductProps[]>([]);
	const [isError, setIsError] = useState(false);

	const countRef = useRef(0);
	const indexRef = useRef(0);

	const [currentCard, setCurrentCard] = useState<CurrentCardProps>({
		model: 'intro',
	});

	// Polling via socket a cada 10s
	useEffect(() => {
		if (statusSocket !== 'connected') return;

		const fetchSales = () => {
			socket.off('resultSalesProducts');
			socket.once(
				'resultSalesProducts',
				(response: { status: string; data: ProductProps[] }) => {
					if (response.status === 'success') {
						setProducts(response.data);
						setIsError(false);
					} else {
						setIsError(true);
					}
				},
			);
			socket.emit('getSalesProducts');
		};

		fetchSales();
		const interval = setInterval(fetchSales, 10_000);

		return () => {
			clearInterval(interval);
			socket.off('resultSalesProducts');
		};
	}, [statusSocket]);

	const allProducts = [...products];
	const productList = allProducts.filter((p) => p.showSales !== 5);
	const ads = allProducts.filter((p) => p.showSales === 5);

	// Atualiza logo do negócio quando ads mudam
	useEffect(() => {
		if (!ads.length) return;
		setSettings({ logo: ads[0]?.imageUrl });
	}, [products]);

	// Tela inicial quando não há produtos
	useEffect(() => {
		if (productList.length) return;

		const interval = setInterval(() => {
			countRef.current += 1;
			setCurrentCard({ model: 'intro' });
		}, 8000);

		return () => clearInterval(interval);
	}, [productList.length]);

	// Auto-reload após 15 min
	useEffect(() => {
		const timeout = setTimeout(
			() => {
				window.location.reload();
			},
			1000 * 60 * 15,
		);

		return () => clearTimeout(timeout);
	}, []);

	function nextCard() {
		const newCount = countRef.current + 1;
		countRef.current = newCount;

		if ((countRef.current - 1) % 5 === 0) {
			const randomIndex = Math.floor(Math.random() * ads.length);
			setCurrentCard({ model: 'ads', content: ads[randomIndex] });
			return;
		}

		if ((countRef.current - 1) % 11 === 0) {
			setCurrentCard({ model: 'intro' });
			return;
		}

		const nextIndex = (indexRef.current + 1) % productList.length;
		indexRef.current = nextIndex;
		const nextProduct = productList[nextIndex];

		if (nextProduct.name.toUpperCase().startsWith('YOUTUBE')) {
			if (hideVideos) {
				nextCard();
				return;
			}
			setCurrentCard({
				model: 'youtube',
				id: nextProduct.id,
				videoId: extractVideoCodeYouTube(nextProduct.description)[0],
				onEnd: nextCard,
			});
			return;
		}

		const card = cards[Math.floor(Math.random() * cards.length)];
		const imgBluesoft = `https://cdn-cosmos.bluesoft.com.br/products/${nextProduct.barcode}`;
		const imageUrl = nextProduct.imageUrl ? nextProduct.imageUrl : imgBluesoft;

		const img = new Image();
		img.src = imageUrl;

		img.onload = () => {
			setCurrentCard({ product: { ...nextProduct, imageUrl }, model: card });
		};

		img.onerror = () => {
			if (nextProduct.imageUrl === imgBluesoft) {
				setCurrentCard({
					model: card,
					product: { ...nextProduct, imageUrl: '/minha-oferta.svg' },
				});
				return;
			}

			img.src = imgBluesoft;
			img.onload = () => {
				setCurrentCard({
					product: { ...nextProduct, imageUrl: img.src },
					model: card,
				});
			};
			img.onerror = () => {
				setCurrentCard({
					model: card,
					product: { ...nextProduct, imageUrl: '/minha-oferta.svg' },
				});
			};
		};
	}

	return (
		<AnimatePresence mode="wait">
			<motion.main
				className="relative h-full w-full overflow-hidden bg-primary text-secondary"
				style={{ containerType: 'size' }}
				key={`${currentCard.model}-${'product' in currentCard ? currentCard.product.id : 'no-product'}`}
				initial={{ opacity: 0, scale: 1.05 }}
				animate={{ opacity: 1, scale: 1 }}
				exit={{ opacity: 0, scale: 1.05 }}
				transition={{ duration: 0.3 }}
			>
				<Card {...currentCard} next={nextCard} />
				<div
					className="fixed bottom-0 right-0 h-3 w-3 rounded-tl-3xl border shadow-md data-[connection=false]:bg-red-500 data-[connection=true]:bg-green-500"
					data-connection={!isError}
				/>
			</motion.main>
		</AnimatePresence>
	);
}

function Card({ next, ...content }: CurrentCardProps) {
	useEffect(() => {
		if (content.model !== 'youtube' && next) {
			const interval = setInterval(next, 8000);
			return () => clearInterval(interval);
		}
	}, []);

	switch (content.model) {
		case 'intro':
			return <Ads name="MINHA OFERTA" imageUrl="" />;
		case 'ads':
			return content.content ? (
				<Ads {...content.content} />
			) : (
				<Ads name="SUPER OFERTAS!!!" imageUrl="" />
			);
		case 'youtube':
			return <CardYouTube {...content} />;
		case 'CardSale1':
		case 'CardSale2':
			if (!content.product) return <></>;
			const Comp = CardsSale[content.model];
			return <Comp {...content.product} />;
		default:
			return <></>;
	}
}
